import * as Sentry from '@sentry/nextjs';
import type { StatusCodes } from 'http-status-codes';
import { getReasonPhrase } from 'http-status-codes';
import Cookies from 'js-cookie';
import { convertQueryParams } from './utils';
import { Logger } from '@icommerce/logger';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@libs/config';
import { APP_NAME } from '@libs/constants';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export const CATALOG_SERVICE_PROXY_PATH = `/_${APP_NAME}/api/catalog-service`;

export type ServiceApiRequestError = Error & {
  message: string;
  statusCode: StatusCodes;
  path: string;
  params?: Record<string, string>;
};

export const getOptions = ({ method = 'GET', headers = {}, ...rest }): RequestInit => ({
  method,
  credentials: 'include',
  headers: new Headers({
    'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN') || '',
    ...headers,
  }),
  ...rest,
});

/**
 * Low level request against the Catalog Service REST API.
 *
 * @param path The REST API path to use.
 * @param isServerSideRequest Set it to true when the request will be executed from a public machine (like the build server for example).
 * Otherwise, the internal API url will be used, which can lead to errors.
 * Important: Session information will only be transferred correctly when setting isServerSideRequest to false and therefore relaying the request
 * via the next api, instead of directly accessing the public api.
 * @param params Optional request parameters as key-value-object
 * @param fetchOptions additional options passed to fetch
 */
export async function serviceApiRequest<ReturnType>(
  path: string,
  isServerSideRequest: boolean,
  params?: Record<string, string | number>,
  fetchOptions = {},
  sendSentryDetails = true
): Promise<ReturnType> {
  const queryParams = convertQueryParams(params);

  if (isServerSideRequest && !publicRuntimeConfig.catalogServiceUrl) {
    throw new Error('no base url set');
  }

  const checkedPath = path.startsWith('/') ? path : `/${path}`;

  // when not being executed during build, rewrite request to catalog-service through next. rewrite is defined in next.config.ts
  const pathPrefix = isServerSideRequest ? publicRuntimeConfig.catalogServiceUrl : CATALOG_SERVICE_PROXY_PATH;

  const url = `${pathPrefix || ''}${checkedPath}${queryParams}`;

  let response: Response;
  const requestStartTime = new Date().getTime();
  try {
    response = await fetch(url, getOptions(fetchOptions));

    if (response.ok) {
      return await (response.json() as Promise<ReturnType>);
    }

    throw {
      message: getReasonPhrase(response.status),
      statusCode: response.status,
      path,
      params,
    } as ServiceApiRequestError;
  } catch (error) {
    const requestEndTime = new Date().getTime();

    // for client side requests only: catch exception for sentry to get more insights
    if (!isServerSideRequest && sendSentryDetails) {
      Sentry.setExtras({
        fetchOptions: getOptions(fetchOptions),
        requestTime: (requestEndTime - requestStartTime) / 1000,
      });
      Sentry.captureException(new Error(`Fetch request failed ${url} - ${(error as Error).message}`));
    }
    // for server side requests
    if (isServerSideRequest) {
      Logger.error(
        {
          url,
          error: JSON.stringify(error),
          message: (error as ServiceApiRequestError)?.statusCode
            ? 'Catalog-service response error'
            : 'Error while trying to request the catalog-service',
          requestTime: (requestEndTime - requestStartTime) / 1000,
        },
        'serviceApiRequest'
      );
    }

    throw error;
  }
}
